'use client'

import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'
import { sessionAdapter } from '@skop-app/lib-domain/adapters'

import { useThirdPartyIdentify } from '@/hooks/useThirdPartyIdentify'
import { useTeamManagement } from '@/hooks/useTeamManagement'
import { getPersonalWorkspaceMembership } from '@/features/team-selection/lib/resolveActiveTeamId'
import { useUser } from '@/services/user/user.query'
import { useOrganizationMetadata } from '@/services/queries/team'
import { ORG_METADATA_ALLOW_REFS_CREATION_KEY } from '@/services/reference/reference.metadata'
import { isRotorTemplate, resolveOrganizationScope } from '@/utils/organization'
import {
  resolveEffectiveAuthorization,
  TEAM_AUTHORIZATION_POLICY_V1_KEY,
} from '@/features/permissions'
import {
  readTeamReviewWorkflowPolicyFromMetadata,
  TEAM_REVIEW_WORKFLOW_POLICY_V1_KEY,
} from '@/features/reviews'
import { normalizeBusinessLanguages } from '@/utils/languagePolicy'
import { UserContextValue } from './userContext.shared'
import { hasEffectiveSeatAccess } from '@/utils/seatAccess'
import type { SeatAwareUserTeam } from '@/types/seatAccess'

interface UserWrapperProps {
  children: ReactNode
}

export function UserWrapper({ children }: Readonly<UserWrapperProps>) {
  const isClient = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  )

  const {
    data: userData,
    isFetching,
    isLoading: isLoadingUser,
    refetch: refetchUser,
  } = useUser()
  const [seatAccessNow, setSeatAccessNow] = useState(Date.now)
  const nearestGraceDeadline = userData?.organizations.reduce<
    number | undefined
  >((nearest, membership: SeatAwareUserTeam) => {
    if (membership.seatAccessState !== 'GRACE') return nearest
    const deadline = Date.parse(membership.seatGraceEndsAt ?? '')
    if (!Number.isFinite(deadline) || deadline <= seatAccessNow) return nearest
    return nearest == null || deadline < nearest ? deadline : nearest
  }, undefined)

  useEffect(() => {
    if (nearestGraceDeadline == null) return
    let timeout: ReturnType<typeof setTimeout>
    const refreshAtDeadline = () => {
      const remaining = nearestGraceDeadline - Date.now()
      if (remaining > 0) {
        timeout = setTimeout(
          refreshAtDeadline,
          Math.min(remaining, 2_147_483_647),
        )
        return
      }
      // Close local rights immediately, even if the user refresh fails.
      setSeatAccessNow((previous) => Math.max(previous, Date.now()))
      void refetchUser()
    }
    refreshAtDeadline()
    return () => clearTimeout(timeout)
  }, [nearestGraceDeadline, refetchUser])

  const {
    team,
    teams,
    organizationMemberships,
    organizationParent,
    isLoadingTeam,
    isLoadingSelectedOrganization,
    isOrganizationHydrationError,
    retryOrganizationHydration,
    selectedTeamId,
    isSolo,
  } = useTeamManagement(userData)
  const retryRequestScopeHydration = useCallback(async () => {
    if (!userData) {
      const result = await refetchUser()
      return { isError: result?.isError ?? true }
    }
    return retryOrganizationHydration()
  }, [refetchUser, retryOrganizationHydration, userData])
  const { data: organizationMetadata = [] } = useOrganizationMetadata(
    selectedTeamId,
    [
      ORG_METADATA_ALLOW_REFS_CREATION_KEY,
      TEAM_AUTHORIZATION_POLICY_V1_KEY,
      TEAM_REVIEW_WORKFLOW_POLICY_V1_KEY,
    ],
  )

  // Format session data
  const {
    subscription,
    authorization: authData,
    ownership,
    customSettings: userSettings,
  } = useMemo(
    () =>
      sessionAdapter({
        team,
        organizationParent,
        userData: userData
          ? {
              ...userData,
              organizations: userData.organizations.filter((membership) =>
                hasEffectiveSeatAccess(membership, seatAccessNow),
              ),
            }
          : undefined,
      }),
    [team, organizationParent, userData, seatAccessNow],
  )
  const customSettingsLanguages = useMemo(() => {
    for (const source of [
      team?.customSettings?.languages,
      organizationParent?.customSettings?.languages,
      userSettings?.languages,
    ]) {
      const languages = normalizeBusinessLanguages(source, [])
      if (languages.length) return languages
    }

    return normalizeBusinessLanguages(undefined)
  }, [
    team?.customSettings?.languages,
    organizationParent?.customSettings?.languages,
    userSettings?.languages,
  ])
  const customSettings = useMemo(
    () => ({
      ...userSettings,
      languages: customSettingsLanguages,
      propertyId:
        userSettings?.propertyId ?? process.env.NEXT_PUBLIC_DEFAULT_PROPERTY!,
    }),
    [customSettingsLanguages, userSettings],
  )
  const isRotor = isRotorTemplate(customSettings.template)

  const isGuest = authData?.user?.role === 'GUEST'
  const isAdmin = authData?.user?.role === 'ADMIN'
  const isSkopAdmin =
    isAdmin && teams.some((t) => t.value === process.env.NEXT_PUBLIC_SKOP_ORGID)

  const {
    authorization,
    authorizationConstraints,
    parsedPolicy: teamAuthorizationPolicyState,
    effectivePolicy: teamAuthorizationPolicy,
  } = useMemo(
    () =>
      resolveEffectiveAuthorization({
        sessionAuthorization: authData,
        metadata: organizationMetadata,
        template: customSettings.template,
      }),
    [authData, customSettings.template, organizationMetadata],
  )
  const teamReviewWorkflowPolicyState = useMemo(
    () => readTeamReviewWorkflowPolicyFromMetadata(organizationMetadata),
    [organizationMetadata],
  )

  const organizationScope = useMemo(
    () =>
      resolveOrganizationScope({
        ownership,
        selectedOrganizationId: selectedTeamId,
        selectedOrganization: team,
        selectedOrganizationParent: organizationParent,
        isSolo,
        personalWorkspaceOrganizationId: isSolo
          ? getPersonalWorkspaceMembership(organizationMemberships)?.value
          : undefined,
      }),
    [
      ownership,
      selectedTeamId,
      team,
      organizationParent,
      isSolo,
      organizationMemberships,
    ],
  )

  // Centralized third-party identification (PostHog, Bugsnag)
  useThirdPartyIdentify({
    user: userData?.user,
    traits: {
      subscriptionId: subscription?.id ?? undefined,
      teamId: team?.id ?? undefined,
      hasShop: Boolean(ownership?.shopID),
      partner: ownership?.partner,
      role: authData?.user?.role,
      isGuest,
      isAdmin,
      scopeKind: organizationScope.scopeKind,
    },
  })

  const userState = useMemo(
    () => ({
      user: userData?.user,
      organizationScope,
      team,
      teamSummary: team,
      teams,
      organizationMemberships,
      selectedTeamId,
      subscription,
      isFetching,
      isLoading: isLoadingUser,
      isLoadingUser,
      isRotor,
      isClient,
      authorization,
      ownership,
      customSettings,
      isLoadingTeam,
      isLoadingSelectedOrganization,
      isOrganizationHydrationError,
      retryOrganizationHydration: retryRequestScopeHydration,
      isGuest,
      isAdmin,
      isSkopAdmin,
      teamAuthorizationPolicy,
      teamAuthorizationPolicySource: teamAuthorizationPolicyState.source,
      teamAuthorizationPolicyError: teamAuthorizationPolicyState.error,
      teamReviewWorkflowPolicy: teamReviewWorkflowPolicyState.policy,
      teamReviewWorkflowPolicySource: teamReviewWorkflowPolicyState.source,
      teamReviewWorkflowPolicyError: teamReviewWorkflowPolicyState.error,
      authorizationConstraints,
    }),
    [
      userData?.user,
      organizationScope,
      team,
      teams,
      organizationMemberships,
      selectedTeamId,
      subscription,
      isFetching,
      isLoadingUser,
      isRotor,
      isClient,
      authorization,
      ownership,
      customSettings,
      isLoadingTeam,
      isLoadingSelectedOrganization,
      isOrganizationHydrationError,
      retryRequestScopeHydration,
      isGuest,
      isAdmin,
      isSkopAdmin,
      teamAuthorizationPolicy,
      teamAuthorizationPolicyState.source,
      teamAuthorizationPolicyState.error,
      teamReviewWorkflowPolicyState.policy,
      teamReviewWorkflowPolicyState.source,
      teamReviewWorkflowPolicyState.error,
      authorizationConstraints,
    ],
  )

  return (
    <UserContextValue.Provider value={userState}>
      {children}
    </UserContextValue.Provider>
  )
}
export { useUserContext } from './userContext.shared'
